import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, flatMap, takeUntil, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';
import { TenantService } from '../services';
import { CreateCatalogDialogComponent } from './createCatalogDialog.component';

@Component({
    styles: [`
        .catalogsPanel {
            overflow-y: auto;
            height: calc(100vh - 137px);
        }
        .cdk-column-title.mat-column-title {
            width: calc(100vw - 800px);
            min-width: calc(100vw - 800px);
        }
        .cdk-column-created.mat-column-created {
            width: 155px;
            min-width: 155px;
        }
        .cdk-column-streamCount.mat-column-streamCount {
            width: 85px;
            min-width: 85px;
        }
        .cdk-column-actions.mat-column-actions {
            width: 65px;
            min-width: 65px;
        }
        mat-chip.small-chip {
            margin: 0px;
            min-height: 25px;
        }
    `],
    template: `
        <div fxLayout="column">
            <mat-toolbar style="height: 70px;">
                <h3>Catalogs</h3>
                <span fxFlex></span>
                <button mat-button color="primary" (click)="add()">Add Catalog</button>
            </mat-toolbar>
            <div *ngIf="noTouching" fxFlex fxLayoutAlign="center center"><spinner></spinner></div>
            <div class="catalogsPanel"
                 infiniteScroll 
                 [infiniteScrollThrottle]="300"
                 [infiniteScrollDistance]="1"
                 [scrollWindow]="false"
                 (scrolled)="nextPage()">
                <table mat-table
                    matSort 
                    (matSortChange)="sort($event)"
                    [matSortDisableClear]="true "
                    [matSortActive]="params?.value?.sort" 
                    [matSortDirection]="params?.value?.sortDirection" 
                    *ngIf="!noTouching" 
                    [dataSource]="catalogs" 
                    style="width: 100%;">
                    <ng-container matColumnDef="title">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="titleLower">Title</th>
                        <td mat-cell *matCellDef="let row">{{row.title}}</td>
                    </ng-container>
                    <ng-container matColumnDef="created">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="created" start="desc" style="padding-left: 15px;">Created</th>
                        <td mat-cell *matCellDef="let row">{{row.created | date: 'short'}}</td>
                    </ng-container>
                    <ng-container matColumnDef="streamCount">
                        <th mat-header-cell *matHeaderCellDef>Streams</th>
                        <td mat-cell *matCellDef="let row">{{row.streams?.length}}</td>
                    </ng-container>
                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef></th>
                        <td mat-cell *matCellDef="let row">
                            <mat-menu #menu="matMenu">
                                <a mat-menu-item [routerLink]="[row.id]">
                                    <mat-icon>build</mat-icon>
                                    <span>Edit</span>
                                </a>
                                <button mat-menu-item (click)="delete(row)"><mat-icon>clear</mat-icon>Delete</button>
                            </mat-menu>
                            <button mat-icon-button class="edit-button" mat-icon-button [matMenuTriggerFor]="menu">
                                <mat-icon>settings</mat-icon>
                            </button>
                        </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="['title', 'created', 'streamCount', 'actions']"></tr>
                    <tr mat-row *matRowDef="let row; columns: ['title', 'created', 'streamCount', 'actions'];"></tr>
                </table>
                <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                    <spinner></spinner>
                </div>
            </div>
        </div>
    `
})
export class CatalogListComponent implements OnInit, OnDestroy{
    noTouching: boolean;
    noTouchingPaging: boolean;
    lastFetched: boolean;
    params = new BehaviorSubject<any>(null);
    catalogs = new BehaviorSubject<any[]>([]);
    private cleanupTime = new Subject<void>();

    constructor(private dialog: MatDialog,
                private snackBar: MatSnackBar,
                private tenantService: TenantService) {}

    ngOnInit(): void {
        this.params
        .pipe(
            filter(params => !!params),
            flatMap(params => this.tenantService.getCatalogs(params)),
            tap(() => this.noTouchingPaging = false),
            tap(catalogs => this.lastFetched = catalogs.last),
            takeUntil(this.cleanupTime)
        ).subscribe(catalogs => {
            this.catalogs.next(this.catalogs.value.concat(catalogs.content));
        });

        this.params.next({  sort: 'titleLower', sortDirection: 'asc', page: 0,  rows: 40 });
    }

    nextPage(): void {
        if (!this.lastFetched) {
            this.fetch(Object.assign({}, this.params.value, { page: this.params.value.page + 1 }));
        }
    }

    sort(sort: Sort): void {
        this.fetch(Object.assign({}, this.params.value, { page: 0, sort: sort.active, sortDirection: sort.direction }), true)
    }

    add(): void {
        this.dialog.open(CreateCatalogDialogComponent).afterClosed()
        .pipe(
            filter(deets => !!deets),
            flatMap(deets => this.tenantService.addCatalog(deets)),
            tap(() => this.snackBar.open(`Catalog created.`, 'OK', { duration: 3000}))
        ).subscribe(() => this.fetch(Object.assign({}, this.params.value, { page: 0}), true));
    }

    delete(row): void {
        this.dialog.open(ConfirmationDialogComponent, { 
            data: {
                title: `Delete ${row.label}?`, 
                actionLabel: 'Delete'}
            }).afterClosed()
        .pipe(
            filter(doIt => !!doIt),
            flatMap(() => this.tenantService.deleteCatalog(row.id)),
            tap(() => this.snackBar.open(`${row.label} has been deleted.`, 'OK', { duration: 3000}))
        ).subscribe(() => this.fetch(Object.assign({}, this.params.value, { page: 0}), true));
    }

    fetch(nextParams, clear = false): void {
        this.noTouchingPaging = true;
        if (clear) {
            this.catalogs.next([]);
        }
        this.params.next(nextParams);
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}